<script lang="ts" setup>

  import { BngBackdrop } from '../backdrop'

  type Props = {
    drawer?: boolean
    noPadding?: boolean
    closable?: boolean
    foreground?: boolean
    closeOnClickOutside?: boolean
    hideBackdrop?: boolean
    data?: object
  }
  const props = withDefaults(defineProps<Props>(), {
    closeOnClickOutside: false,
    hideBackdrop: false,
    data: () => ({}),
  })

  const emit = defineEmits<{
    (e: 'close'): void
  }>()

  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)

  const isRollingUp = ref(false)

  function clickOutside() {
    if (!props.closeOnClickOutside) {
      return
    }
    close()
  }

  function close(): void {
    isRollingUp.value = true
    setTimeout(() => {
      emit('close')
      isRollingUp.value = false
    }, 500)
  }

</script>
<template>
  <div
    class="wrapper"
    :class="{foreground: foreground}"
  >
    <bng-backdrop
      v-if="!hideBackdrop"
      :class="{ hidden: isRollingUp }"
      @close="close"
    />
    <div
      v-click-outside="clickOutside"
      class="lc-modal"
      :class="{
        hidden: isRollingUp,
        drawer: matchDesktop && drawer,
        'no-padding': noPadding,
      }"
    >
      <slot @click="close" />
      <button
        v-if="closable"
        class="close-btn"
        @click.stop="close"
      >
        <SvgIcon
          icon="close"
          :width="16"
          :desktop-width="20"
          :height="16"
          :desktop-height="20"
        />
      </button>
    </div>
  </div>
</template>
<style  scoped>
@keyframes slide {
  0% {
    transform: translateX(100%) translateY(0);
  }

  100% {
    transform: translateX(0) translateY(0);
  }
}

@keyframes slide-in {
  from {
    transform: translateX(0) translateY(0);
  }

  to {
    transform: translateX(100%) translateY(0);
  }
}

@keyframes dropdown {
  0% {
    opacity: 0;
    transform: translateY(-100%) translateX(0);
    visibility: hidden;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 1;
    transform: translateY(0) translateX(0);
    visibility: visible;
  }
}

@keyframes rollup {
  0% {
    opacity: 1;
    transform: translateY(0) translateX(0);
    visibility: visible;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translateY(-100%) translateX(0);
    visibility: hidden;
  }
}

.wrapper {
  &.large {
    .lc-modal {
      left: 0;
      width: 100%;
    }
  }

  @media screen and (width >= 1024px) {
    &.large {
      .lc-modal {
        left: calc(50% - 400px);
        width: 800px;
      }
    }
  }

  &.foreground {
    .lc-modal {
      z-index: 60;
    }

    .lc-backdrop {
      z-index: 50;
    }
  }
}

.lc-modal {
  position: absolute;
  z-index: 20;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  overflow: hidden;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  padding-top: 3.875rem;
  background: white;
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0) translateX(0);
  visibility: visible;

  &:not(.drawer) {
    z-index: 30;
    animation: dropdown 0.5s ease;

    &.hidden {
      animation: rollup 0.5s ease;
      opacity: 0;
      transform: translateY(-100%) translateX(0);
      visibility: hidden;
    }
  }

  .close-btn {
    position: absolute;
    z-index: 2;
    top: 5.5em;
    right: 1.5em;
    padding: 0;
    border-radius: 100%;
    font-size: 1em;

    .c-icon {
      color: var(--color-secondary);
      opacity: 0.5;
    }
  }

  &.no-padding {
    .close-btn {
      top: 5em;
      right: 1.5em;
    }
  }

  @media screen and (width >= 1024px) {
    width: 600px;
    height: unset;
    max-height: calc(100vh - 6em);
    box-sizing: border-box;
    padding: 4em;
    border-radius: 4px;
    box-shadow: 0 0 25px 0 rgb(0 0 0 / 15%);
    inset: 6em auto auto calc(50% - 300px);

    &.no-padding {
      padding: 0;

      .close-btn {
        top: 1.5em;
        right: 3em;
      }
    }

    .close-btn {
      position: absolute;
      top: 4em;
      right: 4em;
      padding: 0;
    }

    &.drawer {
      width: 800px;
      height: auto;
      max-height: 100%;
      padding: 6em;
      animation: slide 0.5s ease;
      inset: 5.5em 0 0 auto;

      &.hidden {
        animation: slide-in 0.5s ease;
        transform: translateX(100%) translateY(0);
      }

      .close-btn {
        position: absolute;
        top: 6em;
        right: 6em;
        padding: 0;
      }
    }
  }

  .lc-backdrop {
    position: absolute;
    top: 0;
  }
}
</style>
